import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import {
    DEFAULT_PIPELINE_STATE,
    describeProperties,
    freelancersOf,
    milestoneProgress,
    newSpecDefaults,
    PIPELINE_STATUSES,
    PRIORITIES,
    positionsBetween,
    priorityMeta,
    SPEC_ASSET_TYPES,
    statusMeta,
} from '../../lib/art-pipeline.js';
import { freelancerLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefPreview } from '../../lib/brief.js';
import { router } from '../../lib/router.js';
import { hasGridState, parseGridState, serializeGridState } from '../../lib/smart-grid.js';
import '../shared/smart-grid.js';
import './pipeline-commission-form.js';
import './pipeline-milestones-panel.js';
import './pipeline-spec-form.js';
import './pipeline-spec-panel.js';

/** Where this browser keeps the viewer's last view of the grid, as its query string. */
const VIEW_STORAGE_KEY = 'd3-assets.pipeline.view';

/** A remembered value, or '' when there's none or storage is blocked. */
function readStored(key) {
    try {
        return localStorage.getItem(key) || '';
    } catch {
        return '';
    }
}

/** Remembers a value; with storage blocked it just isn't remembered. */
function writeStored(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage blocked: nothing to remember it in.
    }
}

/** The viewer's last view, or '' when there's none. */
function readStoredView() {
    return readStored(VIEW_STORAGE_KEY);
}

/** Where this browser remembers whether the viewer wants the pipeline full width. */
const FULL_WIDTH_STORAGE_KEY = 'd3-assets.pipeline.fullWidth';

/** The query parameter that shows archived specs too. */
const ARCHIVED_PARAM = 'archived';

/** The field a grouped column's value is saved under, for a move into its group. */
const GROUP_FIELDS = {
    asset_type: 'asset_type',
    priority: 'priority',
    milestone: 'milestone_id',
};

/**
 * The art pipeline (issue #195): specs in a <smart-grid>. "+ Spec" adds one
 * from a short form; the column headers sort, group and filter; cells edit in
 * place; selected rows bulk-edit. A spec's side panel edits the rest, and its
 * "+ Commission" makes a commission on it, in a form over the grid. "Manage
 * Pipeline" holds the pipeline's settings, for now its milestones.
 */
class ArtPipelineView extends BaseComponent {
    static properties = {
        _items: { type: Array, state: true },
        _milestones: { type: Array, state: true },
        _users: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _notice: { type: String, state: true },
        _grid: { type: Object, state: true },
        _selected: { type: Object, state: true },
        _panelItemId: { type: String, state: true },
        _dialog: { type: String, state: true }, // null, 'spec', 'milestones', 'commission'
        _commissionSpecId: { type: String, state: true },
        _busy: { type: Boolean, state: true },
        _manageOpen: { type: Boolean, state: true },
        _includeArchived: { type: Boolean, state: true },
        _fullWidth: { type: Boolean, state: true },
    };

    static styles = css`
        /* Fill the window under the app's 56px nav and main's 1.5rem padding,
           so the grid takes whatever height the page above it leaves. */
        :host { display: flex; flex-direction: column; height: calc(100vh - 56px - 3rem); }
        smart-grid { flex: 1 1 0; min-height: 16rem; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; gap: 1rem; flex-wrap: wrap; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1rem; }
        .manage { position: relative; }
        .manage .caret { font-size: 0.65rem; margin-left: 0.15rem; }
        .manage-menu {
            position: absolute; top: calc(100% + 0.25rem); right: 0; z-index: 1000; min-width: 12rem;
            background: white; border: 1px solid #d0d5dc; border-radius: 6px; box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.12); padding: 0.3rem 0;
        }
        .manage-menu .item {
            display: flex; align-items: center; gap: 0.5rem; width: 100%; border: 0; background: none;
            padding: 0.35rem 0.75rem; font-size: 0.85rem; text-align: left;
        }
        .manage-menu .item:hover, .manage-menu .item:focus { background: #f1f4f8; outline: none; }
        .toolbar { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 0.6rem; }
        .toolbar .include-archived { margin: 0 0 0 0.5rem; font-size: 0.85rem; white-space: nowrap; }
        .toolbar .full-width { margin-left: auto; white-space: nowrap; }
        .toolbar .search { max-width: 20rem; font-size: 0.85rem; }
        .bulk-bar {
            display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center;
            background: #eef4ff; border: 1px solid #c9dafc; border-radius: 6px;
            padding: 0.5rem 0.75rem; margin-bottom: 0.6rem; font-size: 0.85rem;
        }
        .bulk-bar .form-select { width: auto; font-size: 0.8rem; }
        .badge { font-weight: 500; }
        .muted { color: #999; }
        .offer-tag { font-size: 0.7rem; color: #888; margin-left: 0.25rem; }
        .progress-bar-wrap {
            display: inline-flex; width: 10rem; height: 0.5rem; border-radius: 4px;
            overflow: hidden; background: #e5e7eb; vertical-align: middle; margin-left: 0.75rem;
        }
        .progress-seg { height: 100%; }
        .seg-draft { background: #d1d5db; }
        .seg-commissioned { background: #22d3ee; }
        .seg-assigned { background: #f59e0b; }
        .seg-done { background: #16a34a; }
        .seg-archived { background: #6b7280; }
        .group-meta { font-weight: 400; color: #666; margin-left: 0.5rem; font-size: 0.8rem; }
    `;

    constructor() {
        super();
        this._items = [];
        this._milestones = [];
        this._users = [];
        this._loading = true;
        this._error = null;
        this._notice = null;
        this._grid = { ...DEFAULT_PIPELINE_STATE };
        this._selected = new Set();
        this._panelItemId = null;
        this._dialog = null;
        this._commissionSpecId = null;
        this._busy = false;
        this._manageOpen = false;
        this._includeArchived = false;
        this._fullWidth = false;
        this._onDocClick = (e) => {
            if (
                this._manageOpen &&
                !e.composedPath().some((el) => el.classList?.contains('manage'))
            ) {
                this._manageOpen = false;
            }
        };
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this._onDocClick);
        // A link's view wins; otherwise pick up where this viewer left off.
        const query = router.getQuery();
        // A link to one spec (from its commission's page) opens its panel.
        this._panelItemId = query.get('spec');
        const fromLink = hasGridState(query) || query.has(ARCHIVED_PARAM);
        const params = fromLink ? query : new URLSearchParams(readStoredView());
        this._grid = parseGridState(params, this._columns, DEFAULT_PIPELINE_STATE);
        this._includeArchived = params.get(ARCHIVED_PARAM) === '1';
        this._keepView();
        this._fullWidth = readStored(FULL_WIDTH_STORAGE_KEY) === '1';
        this._askForWidth();
        this._load();
    }

    /** The grid is wide, so the page can take the whole window, as the viewer last chose. */
    _askForWidth() {
        this.dispatchEvent(
            new CustomEvent('layout-width', {
                detail: { full: this._fullWidth },
                bubbles: true,
                composed: true,
            }),
        );
    }

    _toggleFullWidth() {
        this._fullWidth = !this._fullWidth;
        writeStored(FULL_WIDTH_STORAGE_KEY, this._fullWidth ? '1' : '');
        this._askForWidth();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocClick);
    }

    _openFromManage(dialog) {
        this._manageOpen = false;
        this._dialog = dialog;
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            const [specs, milestones] = await Promise.all([
                api.get('/api/specs'),
                api.get('/api/art-milestones'),
            ]);
            this._items = specs.specs || [];
            this._milestones = milestones.milestones || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
        // Freelancers' names come from d3-auth and are slower; the grid works
        // without them, falling back to usernames.
        try {
            const data = await api.get('/api/freelancers?include_staff=true');
            this._users = data.freelancers || [];
        } catch (err) {
            this._error = `Couldn't load freelancers' names: ${err.message}`;
        }
    }

    // --- Columns ---

    get _userLabels() {
        const map = new Map();
        for (const u of this._users) map.set(u.id, freelancerLabel(u));
        return map;
    }

    /** @returns {import('../../lib/smart-grid.js').GridColumn[]} */
    get _columns() {
        const userLabels = this._userLabels;
        const milestones = this._milestones;
        // A spec's type is fixed once it's commissioned: the commission's phases follow from it.
        const uncommissioned = (r) => !r.commissions.length;
        const statusTitle = (r) =>
            r.commissions.length
                ? 'From the spec’s commissions'
                : 'Draft until the spec is commissioned';
        const freelancerTitle = (r) =>
            r.commissions.length
                ? 'From the spec’s commissions'
                : 'A freelancer comes with a commission';
        return [
            {
                key: 'name',
                label: 'Name',
                value: (r) => r.name,
                editor: 'text',
                searchable: true,
                groupable: false,
            },
            {
                key: 'asset_type',
                label: 'Type',
                value: (r) => r.asset_type,
                options: SPEC_ASSET_TYPES,
                order: 'label',
                editor: 'select',
                editable: uncommissioned,
                readonlyTitle: 'Locked: this spec has a commission',
                display: (r) => {
                    const type =
                        SPEC_ASSET_TYPES.find((t) => t.value === r.asset_type)?.label ||
                        r.asset_type;
                    const props = describeProperties(r);
                    return props ? html`${type} <span class="muted">· ${props}</span>` : type;
                },
            },
            {
                key: 'priority',
                label: 'Priority',
                value: (r) => r.priority,
                options: PRIORITIES,
                editor: 'select',
                display: (r) => {
                    const p = priorityMeta(r.priority);
                    return html`<span class="badge ${p.badge}">${p.label}</span>`;
                },
            },
            {
                key: 'status',
                label: 'Status',
                value: (r) => r.status,
                bucket: (r) => ({ key: r.status, label: statusMeta(r.status).label }),
                options: PIPELINE_STATUSES,
                readonlyTitle: statusTitle,
                display: (r) => {
                    const s = statusMeta(r.status);
                    const count =
                        r.commissions.length > 1
                            ? html`<span class="muted"> ×${r.commissions.length}</span>`
                            : nothing;
                    return html`<span class="badge ${s.badge}">${s.label}</span>${count}`;
                },
            },
            {
                key: 'freelancer',
                label: 'Freelancer',
                value: (r) => freelancersOf(r, userLabels)[0]?.id || '',
                bucket: (r) => {
                    const f = freelancersOf(r, userLabels)[0];
                    return { key: f?.id || '', label: f?.label || 'Nobody' };
                },
                order: 'label',
                groupable: true,
                emptyLabel: 'Nobody',
                readonlyTitle: freelancerTitle,
                display: (r) => {
                    const people = freelancersOf(r, userLabels);
                    if (!people.length) return html`<span class="muted">—</span>`;
                    return people.map(
                        (f, i) =>
                            html`${i ? ', ' : ''}${f.label}${f.offered ? html`<span class="offer-tag">(offered)</span>` : nothing}`,
                    );
                },
            },
            {
                key: 'milestone',
                label: 'Milestone',
                value: (r) => r.milestone_id || '',
                bucket: (r) => ({
                    key: r.milestone_id || '',
                    label: r.milestone_name || 'No milestone',
                }),
                // Milestones come soonest first; that's their order.
                options: milestones.map((m) => ({ value: m.id, label: m.name })),
                emptyLabel: 'No milestone',
                editOptions: [
                    { value: '', label: 'No milestone' },
                    ...milestones.map((m) => ({ value: m.id, label: m.name })),
                ],
                editor: 'select',
                allGroups: () => milestones.map((m) => ({ key: m.id, label: m.name, data: m })),
            },
            {
                key: 'brief',
                label: 'Brief',
                value: (r) => r.brief || '',
                display: (r) => briefPreview(r.brief),
                searchable: true,
                sortable: false,
                groupable: false,
                className: 'text-secondary',
                elide: true,
            },
        ];
    }

    // --- Grid state ---

    _onStateChange(e) {
        this._grid = e.detail;
        this._keepView();
    }

    /** Put the view in the URL, to share, and remember it for next time. */
    _keepView() {
        const params = new URLSearchParams(serializeGridState(this._grid, DEFAULT_PIPELINE_STATE));
        if (this._includeArchived) params.set(ARCHIVED_PARAM, '1');
        const qs = params.size ? `?${params}` : '';
        window.history.replaceState({}, '', `/pipeline${qs}`);
        writeStored(VIEW_STORAGE_KEY, qs);
    }

    /** The specs the grid shows: archived ones only when asked for. */
    get _shownItems() {
        return this._includeArchived
            ? this._items
            : this._items.filter((i) => i.status !== 'archived');
    }

    _setIncludeArchived(on) {
        this._includeArchived = on;
        // Hidden specs leave the selection, so a bulk change only touches what's shown.
        const shown = new Set(this._shownItems.map((i) => i.id));
        this._selected = new Set([...this._selected].filter((id) => shown.has(id)));
        this._keepView();
    }

    // --- Saving ---

    _replaceItem(updated) {
        this._items = this._items.map((i) => (i.id === updated.id ? updated : i));
    }

    async _onCellCommit(e) {
        const { row, key, value } = e.detail;
        const changes = {
            name: () => (value ? { name: value } : null),
            asset_type: () => ({ asset_type: value }),
            priority: () => ({ priority: value }),
            milestone: () => ({ milestone_id: value || null }),
        }[key]?.();
        if (!changes) return;
        this._error = null;
        try {
            this._replaceItem(await api.patch(`/api/specs/${row.id}`, changes));
        } catch (err) {
            this._error = err.message;
        }
    }

    async _onRowMove(e) {
        const { rows, prev, next, groupKey } = e.detail;
        const ids = rows.map((r) => r.id);
        const changes = {};
        const field = groupKey != null && GROUP_FIELDS[this._grid.group];
        const groupCol = field && this._columns.find((c) => c.key === this._grid.group);
        if (groupCol) {
            const keyOf = (row) =>
                groupCol.bucket ? groupCol.bucket(row).key : String(groupCol.value(row) ?? '');
            if (rows.some((row) => keyOf(row) !== groupKey)) changes[field] = groupKey || null;
        }
        // Show the new order straight away; the server's answer replaces it.
        const positions = positionsBetween(this._items, prev, next, ids);
        const previous = this._items;
        this._items = this._items
            .map((i) => (ids.includes(i.id) ? { ...i, position: positions[ids.indexOf(i.id)] } : i))
            .sort((a, b) => a.position - b.position);
        this._error = null;
        try {
            const data = await api.post('/api/specs/move', {
                ids,
                after_id: prev?.id ?? null,
                before_id: prev ? null : (next?.id ?? null),
                changes,
            });
            const byId = new Map(data.specs.map((i) => [i.id, i]));
            this._items = this._items
                .map((i) => byId.get(i.id) ?? i)
                .sort((a, b) => a.position - b.position);
        } catch (err) {
            this._items = previous;
            this._error = err.message;
        }
    }

    async _bulkSave(changes) {
        if (this._selected.size === 0) return;
        this._error = null;
        this._busy = true;
        try {
            const data = await api.post('/api/specs/bulk', {
                ids: [...this._selected],
                changes,
            });
            for (const updated of data.specs) this._replaceItem(updated);
            this._notice = `Updated ${data.specs.length} spec${data.specs.length === 1 ? '' : 's'}.`;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    /**
     * Archives or unarchives the selected specs (issue #227). Archived ones drop
     * out of view unless "Include archived?" is on; specs are never deleted.
     * @param {'archive'|'unarchive'} action
     */
    async _bulkArchive(action) {
        const count = this._selected.size;
        if (count === 0) return;
        this._error = null;
        this._busy = true;
        try {
            const data = await api.post(`/api/specs/${action}`, { ids: [...this._selected] });
            for (const updated of data.specs) this._replaceItem(updated);
            this._selected = new Set();
            const verb = action === 'archive' ? 'Archived' : 'Unarchived';
            this._notice = `${verb} ${count} spec${count === 1 ? '' : 's'}.`;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    // --- Rendering ---

    render() {
        if (this._loading) {
            return html`<div class="text-center py-5"><div class="spinner-border text-secondary"></div></div>`;
        }
        const panelItem = this._panelItemId
            ? this._items.find((i) => i.id === this._panelItemId)
            : null;
        return html`
            <div class="page-header">
                <h4>Asset Pipeline</h4>
                <div class="d-flex gap-2">
                    <div class="manage">
                        <button class="btn btn-sm btn-outline-secondary" aria-haspopup="menu" aria-expanded="${this._manageOpen}"
                            @click="${() => (this._manageOpen = !this._manageOpen)}"
                            @keydown="${(e) => e.key === 'Escape' && ((this._manageOpen = false))}">
                            <i class="bi bi-gear"></i> Manage Pipeline <i class="bi bi-caret-down-fill caret"></i>
                        </button>
                        ${
                            this._manageOpen
                                ? html`<div class="manage-menu" role="menu">
                            <button class="item" role="menuitem" @click="${() => this._openFromManage('milestones')}">
                                <i class="bi bi-flag"></i> Milestones</button>
                        </div>`
                                : nothing
                        }
                    </div>
                    <button class="btn btn-sm btn-primary" @click="${() => (this._dialog = 'spec')}">
                        <i class="bi bi-plus-lg"></i> Spec
                    </button>
                </div>
            </div>
            <div class="page-subtitle">Plan, prioritize, commission, and assign asset specifications in the asset pipeline.</div>

            ${this._error ? html`<div class="alert alert-danger py-2 small d-flex justify-content-between">${this._error}<button class="btn-close btn-sm" @click="${() => (this._error = null)}"></button></div>` : nothing}
            ${this._notice ? html`<div class="alert alert-success py-2 small d-flex justify-content-between">${this._notice}<button class="btn-close btn-sm" @click="${() => (this._notice = null)}"></button></div>` : nothing}

            <div class="toolbar">
                <input class="form-control form-control-sm search" type="search" placeholder="Search names and briefs"
                    .value="${this._grid.query}"
                    @input="${(e) => this._onStateChange({ detail: { ...this._grid, query: e.target.value } })}">
                <div class="form-check form-switch include-archived">
                    <input id="include-archived" class="form-check-input" type="checkbox" role="switch"
                        .checked="${this._includeArchived}" @change="${(e) => this._setIncludeArchived(e.target.checked)}">
                    <label class="form-check-label" for="include-archived">Include archived?</label>
                </div>
                <button class="btn btn-sm btn-outline-secondary full-width" aria-pressed="${this._fullWidth}"
                    title="${this._fullWidth ? 'Fit the page to the site width' : 'Use the full window width'}"
                    @click="${this._toggleFullWidth}">
                    <i class="bi ${this._fullWidth ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'}"></i>
                    ${this._fullWidth ? 'Fit width' : 'Full width'}
                </button>
            </div>
            ${this._selected.size > 0 ? this._renderBulkBar() : nothing}

            <smart-grid
                .columns="${this._columns}"
                .rows="${this._shownItems}"
                .state="${this._grid}"
                selectable
                reorderable
                .selected="${this._selected}"
                .rowActions="${(
                    r,
                ) => html`<button class="btn btn-sm btn-link p-0" title="Details, references and commissions"
                    @click="${() => (this._panelItemId = r.id)}"><i class="bi bi-layout-sidebar-reverse"></i></button>`}"
                .groupExtra="${(g, col) => this._renderGroupExtra(g, col)}"
                .emptyMessage="${html`No specs yet. Use <strong>+ Spec</strong> to add the first one.`}"
                @state-change="${this._onStateChange}"
                @selection-change="${(e) => (this._selected = e.detail)}"
                @cell-commit="${this._onCellCommit}"
                @row-move="${this._onRowMove}"></smart-grid>

            ${panelItem ? this._renderPanel(panelItem) : nothing}
            ${this._renderDialog()}
        `;
    }

    _renderGroupExtra(group, col) {
        if (col.key !== 'milestone') return nothing;
        const progress = milestoneProgress(group.rows);
        const m = group.data || this._milestones.find((x) => x.id === group.key);
        return html`
            ${m?.target_date ? html`<span class="group-meta"><i class="bi bi-calendar-event"></i> ${m.target_date}</span>` : nothing}
            ${
                progress.total
                    ? html`<span class="progress-bar-wrap" title="${PIPELINE_STATUSES.map((s) => `${s.label}: ${progress.counts[s.value]}`).join(' · ')}">
                ${PIPELINE_STATUSES.map((s) =>
                    progress.counts[s.value]
                        ? html`<span class="progress-seg seg-${s.value}" style="width: ${(progress.counts[s.value] / progress.total) * 100}%"></span>`
                        : nothing,
                )}
            </span>
            <span class="group-meta">${progress.percentDone}% done</span>`
                    : nothing
            }
        `;
    }

    _renderBulkBar() {
        const bulkSelect = (label, onPick, options) => html`
            <select class="form-select form-select-sm" ?disabled="${this._busy}"
                @change="${(e) => {
                    const value = e.target.value;
                    e.target.value = '__';
                    if (value !== '__') onPick(value);
                }}">
                <option value="__" selected>${label}…</option>
                ${options}
            </select>
        `;
        return html`
            <div class="bulk-bar">
                <strong>${this._selected.size} selected</strong>
                ${bulkSelect(
                    'Set priority',
                    (v) => this._bulkSave({ priority: v }),
                    PRIORITIES.map((p) => html`<option value="${p.value}">${p.label}</option>`),
                )}
                ${bulkSelect(
                    'Set milestone',
                    (v) => this._bulkSave({ milestone_id: v || null }),
                    html`<option value="">No milestone</option>${this._milestones.map((m) => html`<option value="${m.id}">${m.name}</option>`)}`,
                )}
                <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busy}" @click="${() => this._bulkArchive('archive')}"
                    title="Take them out of the pipeline; their commissions stay as they are">
                    <i class="bi bi-archive"></i> Archive
                </button>
                ${
                    this._items.some((i) => this._selected.has(i.id) && i.archived_at)
                        ? html`<button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busy}" @click="${() => this._bulkArchive('unarchive')}">
                            <i class="bi bi-arrow-counterclockwise"></i> Unarchive
                        </button>`
                        : nothing
                }
                <button class="btn btn-sm btn-link" @click="${() => (this._selected = new Set())}">Clear selection</button>
            </div>
        `;
    }

    _renderPanel(spec) {
        return html`<pipeline-spec-panel
            .spec="${spec}"
            .milestones="${this._milestones}"
            .userLabels="${this._userLabels}"
            @spec-changed="${(e) => this._replaceItem(e.detail)}"
            @commission="${(e) => {
                this._commissionSpecId = e.detail;
                this._dialog = 'commission';
            }}"
            @close="${() => (this._panelItemId = null)}"></pipeline-spec-panel>`;
    }

    /** After a commission is made, show its spec with the new commission. */
    async _onCommissionCreated(specId, commission) {
        this._notice = `Commissioned ${commission.slug}.`;
        try {
            this._replaceItem(await api.get(`/api/specs/${specId}`));
        } catch (err) {
            this._error = err.message;
        }
    }

    _renderDialog() {
        const close = () => (this._dialog = null);
        switch (this._dialog) {
            case 'spec':
                return html`<pipeline-spec-form
                    .milestones="${this._milestones}"
                    .defaults="${newSpecDefaults(this._grid)}"
                    @spec-created="${(e) => {
                        this._items = [...this._items, e.detail].sort(
                            (a, b) => a.position - b.position,
                        );
                        this._notice = `Added ${e.detail.name}.`;
                    }}"
                    @close="${close}"></pipeline-spec-form>`;
            case 'commission': {
                const spec = this._items.find((i) => i.id === this._commissionSpecId);
                if (!spec) return nothing;
                return html`<pipeline-commission-form
                    .spec="${spec}"
                    @commission-created="${(e) => this._onCommissionCreated(spec.id, e.detail)}"
                    @close="${close}"></pipeline-commission-form>`;
            }
            case 'milestones':
                return html`<pipeline-milestones-panel
                    .milestones="${this._milestones}"
                    .items="${this._items}"
                    @milestones-changed="${(e) => {
                        this._milestones = e.detail;
                        this._load();
                    }}"
                    @close="${close}"></pipeline-milestones-panel>`;
            default:
                return nothing;
        }
    }
}

customElements.define('art-pipeline-view', ArtPipelineView);
